import { defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTracker } from '/@/core/tracking/api'
import { useCurrentDesigner } from '/@/data/designer/current'
import { usePermission } from '/@/common/tiers'
import { useDevice } from '/@/utils/responsive'
import {
  useProjectGuard,
  type ProjectVerifyOptions,
} from '/@/common/projects/utils'
import { useProjectWizardModal } from '/@/common/projects/modals/ProjectWizardModal'
import type { StellaMenuListItem } from '/@/stella/StellaMenu'
import { stellaModal } from '/@/stella/StellaModal'
import type { ProjectModalTabType } from '/@/common/projects/ProjectModal'
import type { MockupType } from '../../things/types'
import { useInternalSignalApi } from '../../internalSignal/api'
import { getYoutubeLink } from '/@/common/youtube/utils'
import { useMobileGuard } from '/@/common/viz/mobileGuard'
import { useLoading } from '/@/core/elements/loading'
import { useVizApi } from '../../viz/api'
import { throwError } from '/@/core/errors'
import { useToast } from '/@/core/elements/toast'
import { BLANK_MOCKUP_ID } from '/@/common/viz/utils'

export function useToolbarUtils(source: string) {
  const { designer } = useCurrentDesigner()
  const tracker = useTracker()
  const router = useRouter()
  const device = useDevice()
  const mobileGuard = useMobileGuard()
  const projectGuard = useProjectGuard()
  const projectWizardModal = useProjectWizardModal()
  const internalSignalApi = useInternalSignalApi()

  const createProjectPermission = usePermission('CREATE_PROJECTS')
  const createViziPermission = usePermission('CREATE_VIZIS')
  const createPalettePermission = usePermission('CREATE_PALETTES')

  function createMenuListItems(): StellaMenuListItem[] {
    const position = device.isMd ? 'bottom' : 'right'

    return [
      {
        type: 'item',
        id: 'vizi',
        label: 'Vizi',
        action: () => {
          createViziPermission.tierGuard(() => createNewVizi([]), {
            source,
          })
          return false
        },
      },
      {
        type: 'nested-menu',
        id: 'project',
        label: 'Project',
        menu: {
          props: {
            position,
            alignment: 'start',
          },
          items: [
            {
              type: 'item',
              id: 'scratch',
              label: 'From scratch',
              action: () => {
                createProjectPermission.tierGuard(openProjectModal, {
                  source,
                })
                return false
              },
            },
            {
              type: 'item',
              id: 'project wizard',
              label: 'With Project Wizard',
              action: () => {
                createProjectPermission.tierGuard(openProjectWizard, {
                  source,
                })
                return false
              },
            },
          ],
        },
      },
      {
        type: 'item',
        id: 'pal',
        label: 'Color Pal',
        action: () => {
          createPalettePermission.tierGuard(createNewColorPal, {
            source,
          })
          return false
        },
      },
    ]
  }

  function openMenuListItems(): StellaMenuListItem[] {
    const openMenuItems: StellaMenuListItem[] = [
      {
        type: 'item',
        id: 'open-vizi',
        label: 'Vizi',
        action: () => {
          openVizis([])
          return false
        },
      },

      {
        type: 'item',
        id: 'open-project',
        label: 'Project',
        action: () => {
          openProjects()
          return false
        },
      },
      {
        type: 'item',
        id: 'open-pal',
        label: 'Color Pal',
        action: () => {
          openColorPals()
          return false
        },
      },
    ]

    const allDesignsItem: StellaMenuListItem | null = designer.value
      ? {
          type: 'item',
          id: 'designs',
          label: 'All my designs',
          action: () => {
            tracker.send('My Design Dashboard Clicked', {
              component: 'Top Nav',
            })
            router.push({ name: 'designs' })
            return false
          },
        }
      : null

    if (allDesignsItem) openMenuItems.push(allDesignsItem)

    return openMenuItems
  }

  function createAndOpenMenuListItems(type: 'default' | 'tool' = 'default') {
    const position = device.isMd ? 'bottom' : 'right'

    const items: StellaMenuListItem[] = [
      {
        type: 'nested-menu',
        id: 'new',
        label: 'New',
        menu: {
          props: {
            position,
            alignment: 'start',
          },
          items: createMenuListItems(),
        },
      },
      {
        type: 'nested-menu',
        id: 'open',
        label: 'Open',
        menu: {
          props: {
            position,
            alignment: 'start',
          },
          items: openMenuListItems(),
        },
      },
    ]

    if (type === 'default') {
      items.push(
        {
          type: 'divider',
        },
        {
          type: 'nested-menu',
          id: 'help',
          label: 'Help & support',
          icon: 'questionmark-circle',
          menu: {
            props: {
              position,
              alignment: 'start',
            },
            items: [
              {
                type: 'item',
                id: 'help',
                label: 'Help Hub',
                action: () => {
                  internalSignalApi.trackEvent({
                    eventType: 'HELP_HUB_CLICKED',
                  })
                  window.open('https://help.spoak.com/', '_blank')
                },
              },
              {
                type: 'item',
                id: 'video tutorials',
                label: 'Video tutorials',
                action: () => {
                  window.open(
                    getYoutubeLink(
                      'getting started with spoak | demo series',
                      'watch',
                    ),
                  )
                  return false
                },
              },
            ],
          },
        },
      )
    }

    return items
  }

  function openProjects() {
    tracker.send('Projects Viewed', {
      component: source,
    })

    openProjectModal('existing project')
  }

  function openProjectModal(initialTab?: ProjectModalTabType) {
    stellaModal.open({
      content: defineAsyncComponent(
        () => import('/@/common/projects/ProjectModal/ProjectModalContent.vue'),
      ),
      data: {
        initialTab,
      },
    })
  }

  const loading = useLoading()
  const vizApi = useVizApi()
  const route = useRoute()
  const toast = useToast()

  async function createNewVizi(
    viziTypes: MockupType[],
    projectVerifyOptions?: ProjectVerifyOptions,
  ) {
    tracker.send('Viz Clicked', {
      component: source,
      types: viziTypes,
    })

    if (!mobileGuard.featureAllowed.value) {
      stellaModal.open({
        content: defineAsyncComponent(
          () => import('/@/common/viz/modals/MobileGuardModalContent.vue'),
        ),
        data: {
          viziTypes,
        },
      })
    } else {
      try {
        const { project } = await projectGuard.verify(
          'Vizi',
          projectVerifyOptions,
        )

        loading.show()

        // new template assistant flow
        const viziId = await vizApi.create({
          name: 'Untitled',
          projectId: project.id,
          products: [],
          dailyPlayId: route.query.dailyPlayId as string,
          taskId: route.query.taskId as string,
          mockupType: 'MOCKUP',
          templateId: BLANK_MOCKUP_ID,
          designChallengeId: route.query.designChallengeId as string,
          vizVersion: '3',
          setupState: 'REQUIRED',
        })

        if (!viziId) {
          throwError('Vizi create api failed')
          loading.hide()
          toast.show({
            type: 'stella',
            severity: 'critical',
            message:
              'Something went wrong creating your Vizi. Please try again.',
          })
          return
        }

        tracker.send('Vizi Template Assistant Flow Started', {
          id: viziId,
        })
        tracker.send('Vizi Create Clicked', {
          component: 'New Vizi Modal',
          id: viziId,
          viziId,
          viziType: 'MOCKUP',
          blank: true,
          isDailyPlay: Boolean(route.query.dailyPlayId),
          isSchoolTask: Boolean(route.query.taskId),
          isDesignChallenge: Boolean(route.query.designChallengeId),
        })

        router.push({
          name: 'vizi-edit',
          params: {
            viziId,
          },
        })
      } catch {
        return
      }
    }
  }

  async function openVizis(viziTypes: MockupType[]) {
    tracker.send('Vizis Viewed', {
      component: source,
      isPhone: device.isSm,
    })

    stellaModal.open({
      content: defineAsyncComponent(
        () => import('/@/common/viz/modals/VizisModalContent.vue'),
      ),
      data: {
        viziTypes,
      },
    })
  }

  function createNewColorPal() {
    tracker.send('Color Pal Clicked', {
      component: source,
    })
    router.push({
      name: 'palette-create',
    })
  }

  function openColorPals() {
    tracker.send('Color Pals Viewed', {
      component: source,
    })

    stellaModal.open({
      content: defineAsyncComponent(
        () => import('/@/common/modals/PalettesModal/PalettesModalContent.vue'),
      ),
    })
  }

  function openProjectWizard() {
    projectWizardModal.show()
    tracker.send('New Project Clicked', {
      component: 'Designs',
      method: 'with project wizard',
    })
  }

  return {
    createAndOpenMenuListItems,
    createMenuListItems,
    openMenuListItems,
    createNewVizi,
    openProjectWizard,
  }
}
